import { RowSelectionState } from "@tanstack/react-table";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import {
    GroupChildrenState,
    groupKeyFromRowId,
} from "@design-system/ViewTable/grouping/groupByRowTypes";
import { accountItemRowId } from "./integrationsRowId";

export type ChildrenByKey = Record<
    string,
    GroupChildrenState<OrganizationIntegrationAccountItem> | undefined
>;

const itemRowIdsInGroup = (
    childrenByKey: ChildrenByKey,
    groupKey: string,
): string[] =>
    (childrenByKey[groupKey]?.items ?? []).map((item) =>
        accountItemRowId(item),
    );

/** Selected row ids, minus the group headers that stand for other rows. */
export const selectedItemRowIds = (
    selection: Record<string, boolean>,
): string[] =>
    Object.keys(selection).filter(
        (rowId) => selection[rowId] && groupKeyFromRowId(rowId) === null,
    );

/** Group keys whose header checkbox is ticked. */
export const selectedGroupKeys = (
    selection: Record<string, boolean>,
): string[] => {
    const keys: string[] = [];
    for (const rowId of Object.keys(selection)) {
        const groupKey = groupKeyFromRowId(rowId);
        if (selection[rowId] && groupKey !== null) {
            keys.push(groupKey);
        }
    }
    return keys;
};

/**
 * Keeps a group header and its children in step.
 *
 * The table only cascades to the children it has rendered, which for a
 * collapsed or half-paged group is fewer than the group holds — so ticking a
 * header adds what is loaded here and the rest follow as pages arrive, while
 * unticking one clears every child it stood for, loaded or not.
 */
export const syncGroupSelection = ({
    previous,
    next,
    childrenByKey,
}: {
    previous: RowSelectionState;
    next: RowSelectionState;
    childrenByKey: ChildrenByKey;
}): RowSelectionState => {
    const wasSelected = new Set(selectedGroupKeys(previous));
    const isSelected = new Set(selectedGroupKeys(next));

    const synced: RowSelectionState = { ...next };

    for (const groupKey of isSelected) {
        if (wasSelected.has(groupKey)) {
            continue;
        }
        for (const rowId of itemRowIdsInGroup(childrenByKey, groupKey)) {
            synced[rowId] = true;
        }
    }

    for (const groupKey of wasSelected) {
        if (isSelected.has(groupKey)) {
            continue;
        }
        for (const rowId of itemRowIdsInGroup(childrenByKey, groupKey)) {
            delete synced[rowId];
        }
    }

    return synced;
};

/**
 * Rows of a ticked group that aren't selected yet — the pages that landed
 * after the header was ticked.
 */
export const unselectedRowIdsInGroup = ({
    selection,
    childrenByKey,
    groupKey,
}: {
    selection: RowSelectionState;
    childrenByKey: ChildrenByKey;
    groupKey: string;
}): string[] =>
    itemRowIdsInGroup(childrenByKey, groupKey).filter(
        (rowId) => !selection[rowId],
    );

/**
 * Drops a group header that no longer stands for its whole group.
 *
 * A ticked header means every one of that group's loaded rows is selected —
 * `unselectedRowIdsInGroup` keeps re-adopting rows for as long as the header is
 * ticked. So once rows are removed from underneath it (a partial send drops the
 * ones that got an email), the header has to go too: left ticked it would show
 * a selection it no longer stands for, and the adoption effect would pull the
 * removed rows straight back in.
 *
 * A header whose group has no rows loaded yet is kept: it stands for pages that
 * are still arriving rather than for rows that were acted on.
 */
export const pruneStaleGroupHeaders = ({
    selection,
    groupKeyByRowId,
}: {
    selection: RowSelectionState;
    groupKeyByRowId: Map<string, string>;
}): RowSelectionState => {
    const selectedChildCountByGroup = new Map<string, number>();
    const loadedChildCountByGroup = new Map<string, number>();
    for (const [rowId, groupKey] of groupKeyByRowId) {
        loadedChildCountByGroup.set(
            groupKey,
            (loadedChildCountByGroup.get(groupKey) ?? 0) + 1,
        );
        if (selection[rowId]) {
            selectedChildCountByGroup.set(
                groupKey,
                (selectedChildCountByGroup.get(groupKey) ?? 0) + 1,
            );
        }
    }

    return Object.fromEntries(
        Object.entries(selection).filter(([rowId]) => {
            const groupKey = groupKeyFromRowId(rowId);
            if (groupKey === null) {
                return true;
            }
            const loadedCount = loadedChildCountByGroup.get(groupKey);
            return (
                loadedCount === undefined ||
                selectedChildCountByGroup.get(groupKey) === loadedCount
            );
        }),
    );
};
